import { ExternalLink, Facebook, Calendar, Users, MapPin, Clock } from "lucide-react";
import { useStoreInfo } from "@/context/StoreInfoContext";
import { CATEGORY_IMAGES } from "@/lib/api";

const FACEBOOK_EVENTS_URL = "https://www.facebook.com/DugoutCollectibles/events";

export default function Events() {
  const store = useStoreInfo();

  return (
    <div data-testid="events-page">
      <section className="hero-grid">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="chip bg-purple-500/10 text-purple-600">Community</span>
          <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy sm:text-5xl lg:text-6xl">
            Tournaments, leagues & play nights
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-600">
            We run our events, RSVPs, and updates through our Facebook page — that&apos;s where the community
            hangs out. Follow us to see what&apos;s on this week and mark yourself as going.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={FACEBOOK_EVENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fb-events-cta"
              className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
            >
              <Facebook size={16} /> See events on Facebook <ExternalLink size={14} />
            </a>
            {store.facebook_url && (
              <a
                href={store.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fb-follow-cta"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wide text-brand-navy transition hover:bg-brand-navy hover:text-white"
              >
                Follow our page
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          <Card
            icon={<Calendar />}
            title="RSVPs & reminders"
            body="Facebook events let you tap 'Going' and get automatic reminders the day of."
          />
          <Card
            icon={<Users />}
            title="See who's coming"
            body="Check who from your local community is playing before you head over."
          />
          <Card
            icon={<Facebook />}
            title="Chat with organizers"
            body="Comment on the event to ask about format, prizes, or entry fees — we reply fast."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-brand-navy-dark p-8 sm:p-12">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-teal/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="relative grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="chip bg-brand-teal/20 text-brand-teal">Come play with us</span>
              <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">
                Every night is game night
              </h2>
              <p className="mt-4 max-w-md text-white/75">
                Whether it&apos;s Friday Night Magic, Pokémon League, a board game bash, or a Warhammer paint
                & play — you&apos;ll find it on our Facebook.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                {store.address_full && (
                  <li className="flex items-center gap-2">
                    <MapPin size={14} className="text-brand-teal" /> {store.address_full}
                  </li>
                )}
                {store.hours && (
                  <li className="flex items-center gap-2">
                    <Clock size={14} className="text-brand-teal" /> {store.hours}
                  </li>
                )}
              </ul>
              <a
                href={FACEBOOK_EVENTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fb-events-cta-bottom"
                className="btn-accent mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
              >
                <Facebook size={16} /> Browse Facebook events
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img
                src={CATEGORY_IMAGES["board-games"]}
                className="aspect-square rounded-2xl object-cover"
                alt="Board games"
              />
              <img
                src={CATEGORY_IMAGES.mtg}
                className="mt-8 aspect-square rounded-2xl object-cover"
                alt="MTG"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Card({ icon, title, body }) {
  return (
    <div className="card-elevated rounded-2xl p-6">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-lg font-bold text-brand-navy">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{body}</p>
    </div>
  );
}
